import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import ChoixChips from "../../common/ChoixChips/ChoixChips";
import { FicheSection } from "../../common/Fiche";
import { DataTable, EmptyState } from "../../common/DataList";
import PanneauQuotasFinancement from "./PanneauQuotasFinancement";
import ConsommationQuota from "./ConsommationQuota";
import { voirQuotasDeOperateur } from "./operateursApi";
import {
  TERRITOIRES,
  financementsAffiches,
  libellePart,
  partsUtilisees,
  quotaDe,
  resumeCouverture,
  territoireSansSite,
  totalQuotas,
  totalUtilisees,
  utiliseesDe,
} from "./quotasOperateur";

// ============================================================
// Onglet « Communes et quotas » de la fiche opérateur (CE-1369, CE-1418,
// maquette 10) : un tableau de lecture, une ligne par territoire, une colonne
// par financement qui a un quota dans l'année (le FSE+ aujourd'hui, ceux que
// rend l'API) ; un quota vaut pour l'accompagnement total et partiel
// ensemble. Chaque case montre l'anneau de consommation et « utilisées /
// quota » (« 0 / 0 » sans quota), puis la part de chaque accompagnement dès
// qu'une mesure est utilisée ; les mesures utilisées viennent de l'API (zéro
// jusqu'à la sous-orientation, CE-1370). La colonne « Total » n'apparaît qu'à
// partir de deux financements : avec un seul, elle le répéterait. Le même
// tableau pour la Caf et pour l'opérateur connecté (`lectureSeule`). La Caf
// saisit dans un panneau latéral, un financement à la fois (« Modifier
// FSE+ », « Ajouter un quota FSE+ », ou le choix du financement s'il en reste
// plusieurs) ; après l'enregistrement, l'onglet affiche la vue rendue par
// l'API. Une année passée se lit sans action. Un territoire sans site qui a
// des quotas est signalé sans bloquer.
// ============================================================

const AJOUT = "AJOUT";
const MESSAGE_SANS_SITE =
  "Aucun site ne dessert ce territoire : ajoutez-y des communes desservies pour que ces quotas puissent être réalisés.";

const libelleFinancement = (financement) => (
  <>
    {financement.libelle} <span className="cell-secondary">{financement.description}</span>
  </>
);

const OngletQuotas = ({ operateur, onAllerOnglet, onQuotasModifies, lectureSeule = false }) => {
  const [vue, setVue] = useState(null);
  const [erreurChargement, setErreurChargement] = useState(null);
  const [annee, setAnnee] = useState(null);
  const [financementOuvert, setFinancementOuvert] = useState(null);
  const [choixOuvert, setChoixOuvert] = useState(false);
  const boutonAjout = useRef(null);
  const boutonAFocaliser = useRef(null);

  const charger = () => {
    setErreurChargement(null);
    voirQuotasDeOperateur(operateur.id).then(
      (vueChargee) => {
        setVue(vueChargee);
        setAnnee(vueChargee.anneeEnCours);
      },
      (erreur) => setErreurChargement((erreur && erreur.message) || "Une erreur est survenue")
    );
  };

  useEffect(() => {
    charger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operateur.id]);

  // Après la fermeture du panneau, le focus revient au bouton du financement, ou à « Ajouter »
  // (le bouton du choix qui a ouvert le panneau n'existe plus).
  useEffect(() => {
    if (financementOuvert || !boutonAFocaliser.current) return;
    const bouton = (boutonAFocaliser.current !== AJOUT && document.getElementById(boutonAFocaliser.current)) || boutonAjout.current;
    boutonAFocaliser.current = null;
    if (bouton) bouton.focus();
  }, [financementOuvert]);

  if (erreurChargement) {
    return (
      <section className="list-card quotas-operateur">
        <EmptyState
          erreur
          icon="fa-exclamation-triangle"
          title="Les quotas n'ont pas pu être chargés"
          hint={erreurChargement}
          action={{ label: "Réessayer", onClick: charger }}
        />
      </section>
    );
  }
  if (!vue) {
    return (
      <section className="list-card quotas-operateur" aria-busy="true">
        <p className="fiche-operateur__chargement">Chargement des quotas…</p>
      </section>
    );
  }

  const financements = vue.financements;
  const anneeAffichee = vue.annees.find((a) => a.annee === annee) || { annee, modifiable: false, quotas: [], total: 0 };
  const modifiable = anneeAffichee.modifiable && !lectureSeule;
  const affiches = financementsAffiches(financements, anneeAffichee);
  const aAjouter = modifiable ? financements.filter((f) => !affiches.includes(f)) : [];
  const avecTotal = affiches.length > 1;
  const idBoutonModifier = (financementId) => `quotas-modifier-${financementId}`;

  const ouvrirPanneau = (financement) => {
    setChoixOuvert(false);
    boutonAFocaliser.current = affiches.includes(financement) ? idBoutonModifier(financement.id) : AJOUT;
    setFinancementOuvert(financement);
  };

  const fermerChoix = (e) => {
    if (e.key !== "Escape") return;
    setChoixOuvert(false);
    if (boutonAjout.current) boutonAjout.current.focus();
  };

  const quotasEnregistres = (vueAJour) => {
    const financement = financementOuvert;
    // Le quota ajouté fait apparaître « Modifier » et peut faire disparaître « Ajouter » : le focus va à
    // « Modifier », et ne retombe sur l'ajout que si le financement reste sans quota.
    boutonAFocaliser.current = idBoutonModifier(financement.id);
    setVue(vueAJour);
    setFinancementOuvert(null);
    toast.success(`Quotas ${financement.libelle} ${annee} enregistrés.`);
    if (onQuotasModifies) onQuotasModifies();
  };

  const lignes = [
    ...TERRITOIRES.map((t) => {
      const couverture = vue.couverture.find((c) => c.territoire === t.id) || { communes: [], sites: [] };
      return { id: t.id, libelle: couverture.libelle || t.libelle, couverture };
    }),
    // Sans colonne de financement, la ligne des totaux ne dirait que « 0 mesure ».
    ...(affiches.length ? [{ id: "TOTAL", estTotal: true }] : []),
  ];
  const alerte = (ligne) => !ligne.estTotal && territoireSansSite(ligne.couverture, totalQuotas(anneeAffichee, { territoire: ligne.id }));

  const restantes = (quota, utilisees) => {
    const reste = Math.max(quota - utilisees, 0);
    return `${reste} restante${reste > 1 ? "s" : ""}`;
  };

  // « dont 9 en total, 3 en partiel » sous l'anneau, seulement quand des mesures sont utilisées.
  const part = (filtre) => {
    const libelle = libellePart(partsUtilisees(anneeAffichee, filtre));
    return libelle && (
      <>
        {" "}
        <span className="cell-secondary quotas-operateur__part">{libelle}</span>
      </>
    );
  };

  const celluleFinancement = (financement, ligne) => {
    if (ligne.estTotal) {
      const quota = totalQuotas(anneeAffichee, { financement: financement.id });
      const utilisees = totalUtilisees(anneeAffichee, { financement: financement.id });
      return (
        <div className="quotas-operateur__total-financement">
          <ConsommationQuota grand utilisees={utilisees} quota={quota} />{" "}
          <span className="cell-secondary">{restantes(quota, utilisees)}</span>
          {part({ financement: financement.id })}
        </div>
      );
    }
    const quota = quotaDe(anneeAffichee, financement.id, ligne.id);
    return (
      <div className="quotas-operateur__quota">
        <ConsommationQuota utilisees={utiliseesDe(anneeAffichee, financement.id, ligne.id)} quota={quota} />
        {quota !== null && part({ financement: financement.id, territoire: ligne.id })}
      </div>
    );
  };

  const colonnes = [
    {
      key: "territoire",
      header: "Territoire",
      className: "quotas-operateur__col-territoire",
      render: (ligne) => <span className="cell-primary">{ligne.estTotal ? "Total" : ligne.libelle}</span>,
    },
    ...affiches.map((f) => ({
      key: `quota-${f.id}`,
      header: libelleFinancement(f),
      className: "quotas-operateur__col-quota",
      render: (ligne) => celluleFinancement(f, ligne),
    })),
    ...(avecTotal
      ? [
          {
            key: "total",
            header: "Total",
            className: "quotas-operateur__col-quota quotas-operateur__col-total",
            render: (ligne) => {
              if (ligne.estTotal) {
                const quota = totalQuotas(anneeAffichee);
                const utilisees = totalUtilisees(anneeAffichee);
                return (
                  <div className="quotas-operateur__total-financement">
                    <ConsommationQuota grand utilisees={utilisees} quota={quota} />{" "}
                    <span className="cell-secondary">{restantes(quota, utilisees)}</span>
                  </div>
                );
              }
              const total = totalQuotas(anneeAffichee, { territoire: ligne.id });
              return (
                <span className="quotas-operateur__total-territoire">
                  <ConsommationQuota grand utilisees={totalUtilisees(anneeAffichee, { territoire: ligne.id })} quota={total || null} />
                </span>
              );
            },
          },
        ]
      : []),
    {
      key: "communes",
      header: "Communes desservies par ses sites",
      className: "quotas-operateur__col-communes",
      render: (ligne) => {
        if (ligne.estTotal) return null;
        const { couverture } = ligne;
        if (alerte(ligne)) {
          return (
            <div className="quotas-operateur__communes">
              <span className="cell-note--warn quotas-operateur__alerte">
                <i className="fa fa-exclamation-triangle" aria-hidden="true" />
                {MESSAGE_SANS_SITE}
              </span>
            </div>
          );
        }
        if (!couverture.communes.length) {
          return (
            <div className="quotas-operateur__communes">
              <span className="cell-secondary">Aucun site ne dessert ce territoire</span>
            </div>
          );
        }
        return (
          <div className="quotas-operateur__communes">
            {couverture.communes.map((c) => c.nom).join(", ")}{" "}
            <span className="cell-secondary">{resumeCouverture(couverture)}</span>
          </div>
        );
      },
    },
  ];

  const message = () => {
    if (lectureSeule && affiches.length) return ["fa-info-circle", "Quotas fixés par la Caf d'après votre convention."];
    if (!anneeAffichee.modifiable) return ["fa-lock", `Année close : les quotas ${annee} se consultent sans modification.`];
    if (!affiches.length) {
      return [
        "fa-info-circle",
        `Aucun quota saisi pour ${annee}.${modifiable ? " Ajoutez un quota dès que la convention de l'année est signée." : ""}`,
      ];
    }
    return null;
  };
  const [iconeMessage, texteMessage] = message() || [];

  return (
    <div className="quotas-operateur">
      <FicheSection
        id="operateur-quotas"
        title="Quotas de mesures"
        actions={
          <ChoixChips
            name="annee-quotas"
            legend="Année des quotas"
            legendSrOnly
            variant="segmente"
            options={vue.annees.map((a) => ({ id: a.annee, name: String(a.annee) }))}
            value={annee}
            onChange={(valeur) => setAnnee(Number(valeur))}
          />
        }
      >
        <p className="fiche-operateur__secondaire">
          Nombre de mesures d'accompagnement que la Caf finance à {operateur.nom} pour l'année, par financement et par
          territoire, d'après sa convention : un seul quota pour l'accompagnement total et l'accompagnement partiel. Un quota
          non réalisé ne se reporte pas sur l'année suivante.
        </p>
        {texteMessage && (
          <p className="quotas-operateur__message" role="status">
            <i className={`fa ${iconeMessage}`} aria-hidden="true" />
            {texteMessage}
          </p>
        )}
        {modifiable && (
          <div className="quotas-operateur__actions">
            {affiches.map((f) => (
              <button key={f.id} id={idBoutonModifier(f.id)} type="button" className="btn btn-ghost" onClick={() => ouvrirPanneau(f)}>
                <i className="fa fa-pen mr-2" aria-hidden="true" />
                Modifier {f.libelle}
              </button>
            ))}
            {aAjouter.length === 1 && (
              <div className="quotas-operateur__ajout">
                <button ref={boutonAjout} type="button" className="btn btn-ghost" onClick={() => ouvrirPanneau(aAjouter[0])}>
                  <i className="fa fa-plus mr-2" aria-hidden="true" />
                  Ajouter un quota {aAjouter[0].libelle}
                </button>
              </div>
            )}
            {aAjouter.length > 1 && (
              <div className="quotas-operateur__ajout">
                <button
                  ref={boutonAjout}
                  type="button"
                  className="btn btn-ghost"
                  aria-expanded={choixOuvert}
                  aria-controls={choixOuvert ? "quotas-choix-financement" : undefined}
                  onClick={() => setChoixOuvert((ouvert) => !ouvert)}
                >
                  <i className="fa fa-plus mr-2" aria-hidden="true" />
                  Ajouter un quota de financement
                </button>
                {choixOuvert && (
                  <div
                    id="quotas-choix-financement"
                    role="group"
                    aria-labelledby="quotas-choix-financement-titre"
                    className="quotas-operateur__choix"
                    onKeyDown={fermerChoix}
                  >
                    <p id="quotas-choix-financement-titre" className="quotas-operateur__choix-titre">
                      Pour quel financement ?
                    </p>
                    <div className="quotas-operateur__options">
                      {aAjouter.map((f) => (
                        <button key={f.id} type="button" className="btn btn-ghost" onClick={() => ouvrirPanneau(f)}>
                          {libelleFinancement(f)}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        <DataTable
          caption={`Quotas ${annee} de ${operateur.nom} par financement et par territoire`}
          columns={colonnes}
          rows={lignes}
          rowClassName={(ligne) => (ligne.estTotal ? "quotas-operateur__total" : alerte(ligne) ? "row--warn" : null)}
        />
        <p className="fiche-operateur__secondaire quotas-operateur__pied">
          Les communes ne se saisissent pas ici : ce sont les communes desservies de chaque site.
          {!lectureSeule && (
            <>
              {" "}
              <button type="button" className="quotas-operateur__lien" onClick={() => onAllerOnglet && onAllerOnglet("sites")}>
                Modifier les sites
              </button>
            </>
          )}
        </p>
      </FicheSection>
      {financementOuvert && (
        <PanneauQuotasFinancement
          key={`${annee}-${financementOuvert.id}`}
          ouvert
          operateur={operateur}
          annee={anneeAffichee}
          financement={financementOuvert}
          couverture={vue.couverture}
          onFermer={() => setFinancementOuvert(null)}
          onEnregistre={quotasEnregistres}
        />
      )}
    </div>
  );
};

export default OngletQuotas;
